/* ── 206 §4 [RULED — Laurent, SUPERSEDING 205 §4] — THE QUADRANT HEIGHTS, BOTH
 * EISENHOWERS.
 *
 * HIS WORDS: "in My Eisenhower the drawer fills the page, so there is plenty of
 * room left in every quadrant, and in such a case make them NOT SQUARE but all
 * four the same height. If there is not enough place, diminish the emptier row
 * so as to keep everything on screen as much as possible. In a nutshell: avoid
 * empty space in both rows; but if you can't, because of a minimum height or
 * because the page has to be filled, make the two rows match."
 *
 * WHY THIS IS CODE AND NOT A DECLARATION, which is the load-bearing finding and
 * the reason 205 §4 got it half right. The ruling is a CONDITIONAL on a
 * comparison between the two rows' content heights, and CSS grid has no way to
 * express one. Both candidates were worked through against the five clauses:
 *
 *  · `1fr 1fr` (the pre-205 state) — rows always match, so clauses 1, 4 and 5
 *    hold and clause 2 fails: with a definite board the crowded quadrant
 *    scrolls inside itself while its opposite number sits half empty (205
 *    measured 325/325 in the drawer with fourteen cards on the bottom row),
 *    and with an INdefinite board (the matrix) the near-empty row inflates to
 *    the crowded one's height — 906px, all four at 448. That inflation is the
 *    complaint 205 §4 was ruled on.
 *  · `auto auto` (what 205 §4 shipped) — rows are content-sized, so clause 2
 *    holds, and clause 4 fails. `align-content` resolves to `stretch`, which
 *    distributes leftover space EQUALLY among auto tracks: two rows holding 54
 *    and 200 px of cards in a 649 px board come out at 246 and 392. That is
 *    equal GROWTH, not equal HEIGHT, and Laurent's "all four the same height"
 *    asks for the second. It is only invisible when the two rows happen to
 *    hold the same amount, which is the case 205 measured.
 *
 * So the rows are MEASURED and the template is written in pixels. Rule 4 of
 * SESSION_RULES: the primitive did not fit the behaviour, so the primitive is
 * widened rather than the behaviour bent.
 *
 * THE RULE, IN ONE EXPRESSION, AND IT IS THE FULLER ROW THAT IS SOLVED FOR:
 *
 *     the fuller row gets WHAT IT NEEDS, never more, and never less than half.
 *     The emptier row gets the rest.
 *
 *   hiH = max(half, min(hi, avail - lo))      loH = avail - hiH
 *
 * where `avail` is the board minus the one gap, `half` is its half, and
 * `lo`/`hi` are the two rows' NATURAL heights. All five clauses fall out of it
 * with no branch:
 *   1 fills   — the two always sum to `avail`.
 *   2 gives   — `min(hi, ...)` lets a crowded row take everything the emptier
 *               one does not need (`avail - lo`), so no quadrant sits mostly
 *               empty while its opposite overflows.
 *   3 yields  — `avail - lo` is the emptier row keeping its own content, and
 *               `max(half, ...)` stops it keeping more than half once BOTH are
 *               crowded.
 *   4 matches — when both rows fit inside half, `min` returns `hi <= half`
 *               and `max` returns `half`: half and half. The same `max` does
 *               it again from the other side when both rows are over half.
 *   5 not sq. — a quadrant is half a row wide and at most half a board tall;
 *               measured, the drawer's are 615 x 90-612 and the matrix's
 *               617 x 80-717 at 1280x800. Nothing here can produce a square.
 *
 * THE FIRST FORMULATION SOLVED FOR THE EMPTIER ROW INSTEAD — it gave that row
 * its content clamped at half and handed the REST to the fuller one — and its
 * own measurement refuted it: a matrix holding 80 px on top and 178 below on a
 * board at its 360 floor came out 80/274, which is 96 px of blank inside the
 * CROWDED row while the emptier one had none. Solving for the fuller row gives
 * 176/178 there: nothing scrolls, the board is full, and the leftover sits in
 * the row with less in it, which is the only place it can sit without looking
 * like a mistake.
 *
 * THE FLOOR IS LAURENT'S, IS NOT MOVED, AND IS NOT A PARAMETER HERE — WHICH
 * WAS MEASURED, NOT ASSUMED. `TaskMatrix.tsx` carries
 * `minHeight: compact ? 220 : 360` ON THE BOARD, and clause 3 keeps it. The
 * first cut of this module ALSO passed it down as a per-row floor of
 * `(min - gap) / 2`, and the arithmetic refuted that in one line: a matrix
 * holding 30 px on its top row and 448 on its bottom sizes its board to 484
 * — there is no shortage at all — and a 177 px row floor would nonetheless
 * have taken 147 px off the crowded row and made it scroll. The board's own
 * minimum is the whole of the floor, and it reaches the rows through the
 * FIRST branch: when the board sits at 360 with little in it, `hi <= half`
 * holds, and the two rows come out at 177 each. Below that nothing can push
 * them, because a row is never asked for less than its content except when
 * BOTH rows are over half, where the clamp puts them at half and half.
 * A row also has a natural minimum of its own that no figure has to state: a
 * quadrant always renders its heading, and an empty one renders "Drop tasks
 * here" where a card would.
 *
 * HOW THE NATURAL HEIGHTS ARE READ. A row that has been given a height cannot
 * be asked what height it wanted, so the measurement takes a pass with the
 * template at `auto auto` and `align-content: start` — content-sized tracks,
 * nothing stretched — reads the four quadrants, and writes the answer back
 * before the browser paints (`useLayoutEffect`). `align-items` defaults to
 * `stretch`, so within an auto row both quadrants already carry the row's own
 * content height; the max of the pair is taken anyway, so the reading does not
 * depend on that.
 *
 * EVERY FIGURE IS A LAYOUT PIXEL. `offsetHeight` and `clientHeight` answer in
 * layout px under this app's root `zoom`, where `getBoundingClientRect()`
 * answers in VISUAL px — the 142-family trap, and the reason no rect appears
 * anywhere below.
 *
 * `board` is read INSIDE the natural pass and is correct for both hosts
 * without a branch: the dialog's grid is a `flex: 1; minHeight: 0` child, so
 * its height is the flex line's whatever the tracks do; the matrix's grid is
 * in normal flow under its own `minHeight`, so its height IS
 * `max(floor, content)` — which is exactly "the space available" in each case.
 *
 * ── 206B §1 — HIS SCREEN DREW 333 / 657 WHERE THIS RULE SAYS EQUAL ─────────
 * Laurent's Safari, on the 206 bundle, drew My Eisenhower with one card in each
 * top quadrant, none in IMPORTANT and five in SPARE TIME — everything fitting
 * in half the board — at 333 against 657 image px: each row's content plus an
 * EQUAL share of the slack. That is `auto auto` under `align-content:
 * stretch`, the template the natural pass starts from, left standing; the
 * arithmetic above returns two equal rows for those inputs. Chromium does not
 * reproduce it and WebKit does not launch on this box, so NOTHING HERE CLAIMS
 * TO HAVE REPRODUCED IT. Three changes, each closing a way that layout could
 * be left behind:
 *
 *  1.1 THE INSTRUMENT. Every call ended in ONE console line —
 *        [eisen] board N  rows n1 n2 → h1 h2  gap G
 *        [eisen] early return: board N
 *      — what the rule read and what it wrote, in the engine that drew the
 *      screen. 206C §6 — REMOVED: Laurent's glance of 206B found the rows
 *      right on his screen ("fine now"), so the instrument has done its job,
 *      and it logged for EVERY user on every sizing. 1.2 and 1.3 below are
 *      the fix and stay; the line only watched them.
 *  1.2 THE FALLBACK IS EQUAL. A board that measured 0 used to get back the
 *      template it had — on a first run, React's `auto auto`, the very layout
 *      on his screen. The ruling's default is EQUAL, so an unmeasurable board
 *      now gets `EISEN_ROWS_EQUAL`: two `minmax(0, 1fr)` rows, which split the
 *      board evenly and can never grow past it, and need no measurement.
 *  1.3 WHAT RE-RUNS IT. The rule reads three things: the board's height, the
 *      four quadrants' natural heights (heading + cards) and the gap. Before
 *      206B two things re-ran it — every render of the host (the layout
 *      effect) and a ResizeObserver on the BOARD — and the census found two
 *      holes, both measured in Chromium (scripts/drive-206b-glance.js --part=drawer):
 *      (a) THE DRAWER NEVER HAD AN OBSERVER. The effect that attached it had
 *          deps `[ref, gap]`, neither of which ever changes, and
 *          `EisenhowerDialog` is mounted SHUT (it returns null BELOW its
 *          hooks), so the effect ran once — at App's first commit, with a
 *          null ref — and never again. The matrices mount with their grid and
 *          had one. The drawer's rows moved only when App re-rendered.
 *      (b) A CARD THAT GROWS RE-RAN NOTHING, in either host: the observer
 *          watched the board, and a board whose rows are written in px does
 *          not change size when a card inside it does. The named case is a
 *          web font landing after the first layout — `src/fonts.css` serves
 *          all three faces `font-display: swap`, so a card first lays out in
 *          a fallback face and re-flows when the real one arrives.
 *      So the LAYOUT EFFECT now (re)attaches the observation whenever the grid
 *      or its content changes — the drawer gets its observer on the render
 *      that opens it and loses it on the render that shuts it — and the
 *      observer watches the board AND every content box of every quadrant:
 *      each child of its heading row and of its card list (the heading text,
 *      each card, the empty-state line), the boxes whose height is content and
 *      not row. The quadrants and the card lists are deliberately NOT watched:
 *      their size IS the rows', so watching them would re-run the rule on its
 *      own writes. There is no open transition to wait for — the drawer's pane
 *      is a plain fixed div, inline-styled, with no class, `transition` or
 *      `animation` — and if one is ever added, the board observer sees it.
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import type { MutableRefObject, RefObject } from "react";

/** The two row heights, in layout px, for a board of `board` px holding rows of
 *  natural height `n1`/`n2` with one `gap` between them. Exported for the drive
 *  that measures it: it is pure arithmetic and needs no browser to be checked. */
export function eisenRowHeights(
  board: number,
  n1: number,
  n2: number,
  gap: number,
): [number, number] {
  const avail = Math.max(0, board - gap);
  const half = avail / 2;
  const hi = Math.max(n1, n2);
  const lo = Math.min(n1, n2);
  // The fuller row: what it needs, never more than the emptier row can spare,
  // never less than half. The emptier row takes the rest, so the two always
  // sum to the board.
  const hiH = Math.round(Math.max(half, Math.min(hi, avail - lo)));
  const loH = avail - hiH;
  return n1 >= n2 ? [hiH, loH] : [loH, hiH];
}

/** 206B §1.2 — the template for a board that cannot be measured: two EQUAL rows
 *  within it, the ruling's default. Never the template that was there before. */
export const EISEN_ROWS_EQUAL = "minmax(0, 1fr) minmax(0, 1fr)";

/** Measure one four-quadrant grid and write its row template. The DOM half of
 *  the rule, exported so the drive that measures §4 runs THIS code and not a
 *  copy of it (`scripts/drive-206-eisen-rows.js`); `useEisenRows` is the React
 *  half and calls nothing else. */
export function sizeEisenRows(el: HTMLElement, gap: number): void {
  const quads = Array.from(el.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement,
  );
  if (quads.length < 4) {
    // Not a board — nothing to size. Never true of the two hosts.
    return;
  }
  const prevAlign = el.style.alignContent;
  // THE NATURAL PASS — content-sized tracks, nothing stretched.
  el.style.gridTemplateRows = "auto auto";
  el.style.alignContent = "start";
  const n1 = Math.max(quads[0].offsetHeight, quads[1].offsetHeight);
  const n2 = Math.max(quads[2].offsetHeight, quads[3].offsetHeight);
  const board = el.clientHeight;
  el.style.alignContent = prevAlign;
  if (board <= 0) {
    /* 206B §1.2 — EQUAL, NOT "WHAT WAS THERE". This arm restored the template
       it found, and on a first run that is the natural pass's own `auto auto`
       — content plus an equal share of the slack, which is the layout his
       screen showed. The ruling's default is equal rows, and `minmax(0, 1fr)`
       twice IS equal rows within the board, with nothing to measure. */
    el.style.gridTemplateRows = EISEN_ROWS_EQUAL;
    return;
  }
  const [h1, h2] = eisenRowHeights(board, n1, n2, gap);
  el.style.gridTemplateRows = `${String(h1)}px ${String(h2)}px`;
}

/** 206B §1.3 — what the observer watches: the board, and in each quadrant every
 *  child of each of its parts (the heading row's text; the card list's cards
 *  and its empty-state line) — the boxes whose height is CONTENT. Not the
 *  quadrants and not the lists: their size is the rows', so watching them
 *  would re-run the rule on its own writes. */
function eisenWatchTargets(el: HTMLElement): Element[] {
  const out: Element[] = [el];
  for (const quad of Array.from(el.children)) {
    for (const part of Array.from(quad.children)) {
      out.push(...Array.from(part.children));
    }
  }
  return out;
}

const sameTargets = (a: readonly Element[], b: readonly Element[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

interface EisenWatch {
  ro: ResizeObserver;
  targets: Element[];
  gap: number;
}

/** One observer over `targets`, re-sizing the grid `ref` holds. */
function watchEisenRows(
  ref: RefObject<HTMLElement | null>,
  targets: Element[],
  gap: number,
): EisenWatch {
  let queued = false;
  const ro = new ResizeObserver(() => {
    /* A frame's grace, and a re-entrancy flag: the natural pass changes this
       box and puts it back inside one frame, so an observer that acted on its
       own writes would never settle. */
    if (queued) return;
    queued = true;
    /* `globalThis.` and not the bare global: `no-undef` knows neither
       `requestAnimationFrame` nor `window` under this config (measured, both
       ways), and a new src/ file carries ZERO warnings (186). `globalThis`
       is the one spelling the rule accepts; same call, same frame. */
    globalThis.requestAnimationFrame(() => {
      queued = false;
      if (ref.current) sizeEisenRows(ref.current, gap);
    });
  });
  for (const t of targets) ro.observe(t);
  return { ro, targets, gap };
}

function dropEisenWatch(watch: MutableRefObject<EisenWatch | null>): void {
  watch.current?.ro.disconnect();
  watch.current = null;
}

/** Size the two rows of a four-quadrant grid per 206 §4. The ref is the GRID
 *  element itself, whose four children are the quadrants in reading order.
 *  Runs after every render (the card counts are what move), and — 206B §1.3 —
 *  whenever the board or any content box in it changes size, through an
 *  observer the layout effect attaches, re-attaches and drops as the grid and
 *  its cards come and go. The board's own `minHeight` stays where it is
 *  declared and is read here through `clientHeight`, so this takes no floor
 *  of its own. */
export function useEisenRows(ref: RefObject<HTMLElement | null>, gap: number): void {
  const watch = useRef<EisenWatch | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      // Shut (the drawer) or not yet mounted: no board, so nothing to watch.
      dropEisenWatch(watch);
      return;
    }
    sizeEisenRows(el, gap);
    if (typeof ResizeObserver === "undefined") return;
    /* 206B §1.3 — THE OBSERVER IS (RE)ATTACHED HERE, and not in a
       mount-only effect: that shape is what left the drawer without one. The
       target list is compared by identity, so a render that changes nothing
       (App re-renders on every tick) re-attaches nothing; a card that comes
       or goes, the grid remounting, or the gap changing rebuilds it. */
    const targets = eisenWatchTargets(el);
    const w = watch.current;
    if (w?.gap === gap && sameTargets(w.targets, targets)) return;
    dropEisenWatch(watch);
    watch.current = watchEisenRows(ref, targets, gap);
  });
  useEffect(
    () => () => {
      dropEisenWatch(watch);
    },
    [],
  );
}
